import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Brain, Cpu, Database, CheckCircle2, Clock, CalendarClock } from 'lucide-react';
import { Card } from '../../ui/card';
import { Badge } from '../../ui/badge';

const iconMap = {
  Sparkles,
  Brain,
  Cpu,
  Database,
};

const statusConfig = {
  completed: { icon: CheckCircle2, color: 'text-emerald-400', bg: 'bg-emerald-500/20', label: 'Completed' },
  'in-progress': { icon: Clock, color: 'text-amber-400', bg: 'bg-amber-500/20', label: 'In Progress' },
  planned: { icon: CalendarClock, color: 'text-slate-400', bg: 'bg-slate-500/20', label: 'Planned' },
};

const RnDSlide = ({ data }) => {
  return (
    <div className="w-full max-w-6xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-8"
      >
        <Badge className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-300 border-purple-500/30 px-4 py-1 mb-4">
          Research & Development
        </Badge>
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-3">{data.title}</h2>
        <p className="text-lg text-slate-400">{data.subtitle}</p>
      </motion.div>

      {/* Main Project */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/30 p-6 mb-6">
          <h3 className="text-2xl font-bold text-white mb-2">{data.mainProject.title}</h3>
          <p className="text-slate-300 mb-6 max-w-3xl">{data.mainProject.description}</p>
          
          {/* Timeline */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {data.mainProject.phases.map((phase, index) => {
              const status = statusConfig[phase.status];
              const StatusIcon = status.icon;
              return (
                <div
                  key={index}
                  className={`p-4 rounded-xl ${status.bg} border border-slate-700/50`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <StatusIcon className={`w-4 h-4 ${status.color}`} />
                    <span className={`text-xs font-medium ${status.color}`}>{status.label}</span>
                  </div>
                  <p className="text-white font-semibold text-sm mb-1">{phase.phase}: {phase.title}</p>
                  <p className="text-slate-400 text-xs">{phase.desc}</p>
                </div>
              );
            })}
          </div>
        </Card>
      </motion.div>

      {/* Capabilities */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {data.capabilities.map((cap, index) => {
          const Icon = iconMap[cap.icon];
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 + index * 0.1 }}
            >
              <Card className="bg-slate-800/50 border-slate-700/50 p-4 h-full hover:border-purple-500/30 transition-colors">
                <div className="p-2 bg-purple-500/10 rounded-lg inline-block mb-3">
                  <Icon className="w-5 h-5 text-purple-400" />
                </div>
                <h4 className="text-white font-semibold text-sm mb-1">{cap.title}</h4>
                <p className="text-slate-400 text-xs">{cap.desc}</p>
              </Card>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default RnDSlide;
